import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { TrendingUp } from 'lucide-react';

const StatisticsCard = ({ results, filteredResults }) => {
  // Calculate repute distribution from filtered results
  const reputeData = useMemo(() => {
    if (!filteredResults || filteredResults.length === 0) {
      return [];
    }

    const counts = {
      good: 0,
      neutral: 0,
      bad: 0
    };

    filteredResults.forEach(snp => {
      if (snp.repute === 'good') counts.good++;
      else if (snp.repute === 'bad') counts.bad++;
      else counts.neutral++;
    });

    return [
      { name: 'Beneficial', value: counts.good, color: '#10b981' },
      { name: 'Neutral', value: counts.neutral, color: '#6b7280' },
      { name: 'Risk Factors', value: counts.bad, color: '#ef4444' }
    ].filter(item => item.value > 0);
  }, [filteredResults]);

  const COLORS = {
    'Beneficial': '#10b981',
    'Neutral': '#6b7280',
    'Risk Factors': '#ef4444'
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / filteredResults.length) * 100).toFixed(1);
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold" style={{ color: data.payload.color }}>
            {data.name}
          </p>
          <p className="text-sm text-gray-600">
            {data.value} SNPs ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-6 h-6 text-green-600" />
        <h2 className="text-2xl font-semibold text-gray-900">Statistics</h2>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="text-3xl font-bold text-green-700">
            {results.totalSnps.toLocaleString()}
          </div>
          <div className="text-sm text-green-600">Total SNPs</div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="text-3xl font-bold text-blue-700">
            {results.analyzedSnps}
          </div>
          <div className="text-sm text-blue-600">In Database</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
          <div className="text-3xl font-bold text-purple-700">
            {filteredResults.length}
          </div>
          <div className="text-sm text-purple-600">Filtered</div>
        </div>
      </div>

      {/* Pie Chart */}
      {reputeData.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Genetic Profile Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={reputeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {reputeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Legend with counts */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            {reputeData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: item.color }}
                />
                <div className="text-sm">
                  <div className="font-semibold text-gray-700">{item.value}</div>
                  <div className="text-xs text-gray-500">{item.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsCard;